import type { DashboardLayoutItem } from '../../../lib/api';
import type { WidgetDef } from './types';

/**
 * Damon 11.10.2026: Die Widgets „Betreuungskräfte" (caregivers),
 * „Pflegebedürftige" (pflege) und „Anfragen" (anfragen) sind entfernt.
 * Gespeicherte Layouts mit diesen IDs werden ignoriert (knownLayout).
 */
export const REMOVED_WIDGET_IDS = ['caregivers', 'pflege', 'anfragen'] as const;

export const WIDGETS: WidgetDef[] = [
  // CRM-1.0-Übernahme: "Dringend benötigte Betreuungen" (oben links).
  { id: 'urgent-cares', titleKey: 'dashboard.widget.urgent_cares.title', countTitleKey: 'dashboard.widget.urgent_cares.title_count', badgeField: 'urgent_users', targetPage: 'pflege', createTarget: '', defaultW: 7, defaultH: 7, minW: 4, minH: 4 },
  // Globale Aufgaben mit Schalter "Nur meine / Alle".
  { id: 'tasks',      titleKey: 'dashboard.widget.tasks.title',      countTitleKey: 'dashboard.widget.tasks.title_count', targetPage: 'dashboard', createTarget: '', defaultW: 5, defaultH: 7, minW: 3, minH: 4 },
  // Dispo: Verfügbarkeit der BK (Verfügbarkeit / Urlaub) — prominent direkt unter der ersten Zeile.
  { id: 'bk-availability', titleKey: 'dashboard.widget.bk_availability.title', countTitleKey: 'dashboard.widget.bk_availability.title_count', targetPage: 'betreuung', createTarget: '', defaultW: 12, defaultH: 5, minW: 4, minH: 3 },
  { id: 'kpi',        titleKey: 'dashboard.widget.kpi.title',        targetPage: 'dashboard',   createTarget: '',           defaultW: 12, defaultH: 3, minW: 4, minH: 2 },
  { id: 'today',      titleKey: 'dashboard.widget.today.title',      targetPage: 'kalender',    createTarget: 'kalender',   defaultW: 4,  defaultH: 4, minW: 3, minH: 3 },
  { id: 'pipeline',   titleKey: 'dashboard.widget.pipeline.title',   targetPage: 'anfragen',    createTarget: '',           defaultW: 6,  defaultH: 4, minW: 3, minH: 3 },
  // Feedback 11.10.2026 — PB-Ticker ersetzt die Kachel „Pflegebedürftige".
  { id: 'pb-ticker',  titleKey: 'dashboard.widget.pb_ticker.title',  targetPage: 'pflege',      createTarget: '',           defaultW: 6,  defaultH: 5, minW: 3, minH: 3 },
  { id: 'email',      titleKey: 'dashboard.widget.email.title',      targetPage: 'email',       createTarget: '',           defaultW: 6,  defaultH: 4, minW: 3, minH: 3 },
  // Batch-13 — VBS-Dashboard: Anrufliste (Anfragen, die angerufen werden
  // müssen, sortiert nach letztem Anrufversuch).
  { id: 'call-list',  titleKey: 'dashboard.widget.call_list.title',  targetPage: 'anfragen',    createTarget: '',           defaultW: 6,  defaultH: 4, minW: 3, minH: 3 },
];

export function buildDefaultLayout(): DashboardLayoutItem[] {
  return [
    { i: 'urgent-cares', x: 0, y: 0,  w: 7,  h: 7 },
    { i: 'tasks',      x: 7,  y: 0,  w: 5,  h: 7 },
    { i: 'bk-availability', x: 0, y: 7, w: 12, h: 5 },
    { i: 'kpi',        x: 0,  y: 12, w: 12, h: 3 },
    { i: 'today',      x: 0,  y: 15, w: 6,  h: 4 },
    { i: 'pipeline',   x: 6,  y: 15, w: 6,  h: 4 },
    { i: 'pb-ticker',  x: 0,  y: 19, w: 6,  h: 5 },
    { i: 'email',      x: 6,  y: 19, w: 6,  h: 4 },
    { i: 'call-list',  x: 0,  y: 24, w: 6,  h: 4 },
  ];
}

/**
 * Gespeichertes Layout auf bekannte Widgets beschränken und fehlende mit den
 * Default-Positionen ergänzen. Einträge entfernter oder unbekannter Widgets
 * (z. B. „pflege" aus alten Konfigurationen) fallen heraus.
 */
export function effectiveDashboardLayout(stored: DashboardLayoutItem[] | null | undefined): DashboardLayoutItem[] {
  const defaults = buildDefaultLayout();
  const known = new Set(WIDGETS.map(w => w.id));
  const kept = (stored ?? []).filter(l => known.has(l.i));
  if (kept.length === 0) return defaults;
  const have = new Set(kept.map(l => l.i));
  return [...kept, ...defaults.filter(d => !have.has(d.i))];
}
